/**
 * Every admin screen: its address, the browser tab title it gets and what it
 * is for. The tab titles tell people (and AI assistants with many tabs open)
 * where each tab is; the AI guide at /llms.txt lists these as its map of
 * Genesis. A test checks that every route in Admin.jsx is covered, so the map
 * can't drift from the app.
 */

export type Screen = {
  /** The address as written in the guide; :id stands for a page or widget id. */
  path: string;
  /** Matches the pathname (no query string). */
  match: RegExp;
  /** Browser tab title, before " · Genesis". */
  title: string;
  /** What you do there, for the guide. */
  purpose: string;
  /** Who can use it, when not everyone can. */
  who?: string;
  /** Left out of the guide for people who can't use it. */
  only?: 'superAdmin' | 'deployer' | 'notAixcDeployer';
};

const ID = '[^/]+';

// Order matters: the first match wins, so specific addresses come before general ones.
export const SCREENS: Screen[] = [
  {
    path: '/admin',
    match: /^\/admin\/?$/,
    title: 'Pages',
    purpose: 'Find and open pages. Search titles and paths with /admin?q=<text>; choose the site with ?site=ff (default), aixc or ffai. “New page” is in the header.'
  },
  {
    path: '/admin/page/new',
    match: /^\/admin\/page\/new\/?$/,
    title: 'New page',
    purpose: 'Choose the editor for a new page: “Continue with Genesis” (templates and widgets) or “Continue with Studio” (your own HTML, CSS and JavaScript).'
  },
  {
    path: '/admin/template-browser',
    match: /^\/admin\/template-browser\/?$/,
    title: 'Choose a template',
    purpose: 'Pick the template for a new Genesis page, then “Use “<name>””.'
  },
  {
    path: '/admin/page/new/studio',
    match: /^\/admin\/page\/new\/studio\/?$/,
    title: 'New Studio page',
    purpose: 'Create a Studio page: title, category and path. Add ?category=<category path> to choose the category.'
  },
  {
    path: '/admin/page/new/:templateId',
    match: new RegExp(`^/admin/page/new/${ID}/?$`),
    title: 'Create page',
    purpose: 'Create a Genesis page from a template: title, category and path. Add ?category=<category path> to choose the category.'
  },
  {
    path: '/admin/page/copy/:id',
    match: new RegExp(`^/admin/page/copy/${ID}/?$`),
    title: 'Copy page',
    purpose: 'Copy a page to a new path (the slug is required).',
    who: 'super admins and editors of the page’s category, not basic editors'
  },
  {
    path: '/admin/page/versions/:id',
    match: new RegExp(`^/admin/page/versions/${ID}/?$`),
    title: 'Version history',
    purpose: 'A page’s saved versions: preview one, restore it into the draft or ask for review of it.'
  },
  {
    path: '/admin/page/:id/edit-meta',
    match: new RegExp(`^/admin/page/${ID}/edit-meta/?$`),
    title: 'Page metadata',
    purpose: 'Title, description and sharing (OpenGraph) fields; for Studio pages, opened from “SEO & sharing”. After creating a Genesis page, the same form appears below the create form.'
  },
  {
    path: '/admin/page/:id/analytics',
    match: new RegExp(`^/admin/page/${ID}/analytics/?$`),
    title: 'Page analytics',
    purpose: 'Visits and clicks for a live page.'
  },
  {
    path: '/admin/page/:id/preview/:version',
    match: new RegExp(`^/admin/page/${ID}/preview/${ID}/?$`),
    title: 'Earlier version',
    purpose: 'An earlier version of a page in the editor, read-only.'
  },
  {
    path: '/admin/page/:id',
    match: new RegExp(`^/admin/page/${ID}/?$`),
    title: 'Page editor',
    purpose: 'Edit a Genesis page: add, edit, move and delete sections, page settings, save the draft and publish.'
  },
  {
    path: '/admin/studio/sso/:id',
    match: new RegExp(`^/admin/studio/sso/${ID}/?$`),
    title: 'Studio',
    purpose: 'Opens a Studio page in Studio (a separate app), signed in as you; it goes straight there.'
  },
  {
    path: '/admin/approvals',
    match: /^\/admin\/approvals(\/[^/]*)?\/?$/,
    title: 'Approvals',
    purpose: 'Requests to publish, withdraw or delete pages, newest first.'
  },
  {
    path: '/admin/review/:id',
    match: new RegExp(`^/admin/review/${ID}/?$`),
    title: 'Review',
    purpose: 'Compare a requested version with the live page (a page that isn’t live is shown whole), then approve or decline it.',
    who: 'approvers; AIXC deployers may approve AIXC pages but not decline'
  },
  {
    path: '/admin/deploy',
    match: /^\/admin\/deploy(\/.*)?$/,
    title: 'Deploy',
    purpose: 'Approved changes waiting to go live, scheduled deployments and deployment history.',
    who: 'super admins and AIXC deployers',
    only: 'deployer'
  },
  {
    path: '/admin/categories',
    match: /^\/admin\/categories(\/.*)?$/,
    title: 'Categories',
    purpose: 'The category tree. A page’s category decides its path prefix and who may edit it.',
    only: 'notAixcDeployer'
  },
  {
    path: '/admin/widgets/builder/:id',
    match: new RegExp(`^/admin/widgets/builder/${ID}/?$`),
    title: 'Widget builder',
    purpose: 'Edit a custom widget’s HTML, CSS, JavaScript and props; save the draft and publish versions.',
    who: 'super admins',
    only: 'superAdmin'
  },
  {
    path: '/admin/widgets',
    match: /^\/admin\/widgets\/?$/,
    title: 'Custom widgets',
    purpose: 'Reusable widgets made from HTML, CSS and JavaScript.',
    who: 'super admins',
    only: 'superAdmin'
  },
  {
    path: '/admin/presets',
    match: /^\/admin\/presets(\/.*)?$/,
    title: 'Widget presets',
    purpose: 'Saved widget settings shared by many pages. Editing one changes every page that uses it at its next deploy.',
    who: 'super admins',
    only: 'superAdmin'
  },
  {
    path: '/admin/assets',
    match: /^\/admin\/assets(\/.*)?$/,
    title: 'Assets',
    purpose: 'Images, videos and files. Open a folder with ?path=<folder>/; select a file and use “Copy address” for its public URL.',
    only: 'notAixcDeployer'
  },
  {
    path: '/admin/roles',
    match: /^\/admin\/(roles|role\/add|role\/[^/]+)\/?$/,
    title: 'People & roles',
    purpose: 'Who can use Genesis and what each person may do.',
    who: 'super admins',
    only: 'superAdmin'
  },
  {
    path: '/admin/settings',
    match: /^\/admin\/settings(\/.*)?$/,
    title: 'Settings',
    purpose: 'Site-wide values (super admins) and the house style for pasted sections.'
  },
  {
    path: '/admin/ai-guide',
    match: /^\/admin\/ai-guide\/?$/,
    title: 'AI guide',
    purpose: 'This guide: how to set up an AI tool for Genesis, and (super admins) editing it.'
  },
  {
    path: '/admin/connect',
    match: /^\/admin\/connect\/?$/,
    title: 'Connect your AI',
    purpose: 'The connector address to add Genesis to Claude, ChatGPT and other AI apps, and the person’s connected apps (Disconnect).'
  },
  {
    path: '/admin/login',
    match: /^\/admin\/login\/?$/,
    title: 'Sign in',
    purpose: 'Sign-in page. Ask the person to sign in; never type a password yourself.'
  }
];

export const findScreen = (pathname: string): Screen | null => {
  const clean = String(pathname || '').split(/[?#]/)[0];
  return SCREENS.find(s => s.match.test(clean)) || null;
};

/** The browser tab title for an admin address, e.g. "Pages · Genesis". */
export const screenTitle = (pathname: string): string => {
  const screen = findScreen(pathname);
  return screen ? `${screen.title} · Genesis` : 'Genesis';
};
